import React, { useState, useRef, useEffect } from 'react';

export interface DropdownButtonProps {
  /**
   * The dropdown button label
   */
  title: React.ReactNode;
  /**
   * Dropdown menu items (as React nodes)
   */
  children: React.ReactNode;
  /**
   * Optional inline style overrides for the container
   */
  style?: React.CSSProperties;
  /**
   * Align dropdown menu to right edge of button (default: false, aligns left)
   */
  menuAlignRight?: boolean;
  /**
   * Legacy prop name for menuAlignRight (deprecated, use menuAlignRight)
   */
  pullRight?: boolean;
  /**
   * Hide the caret/arrow icon
   */
  noCaret?: boolean;
  /**
   * Button ID
   */
  id?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Button style variant (e.g., 'link')
   */
  bsStyle?: string;
  /**
   * Disable the button
   */
  disabled?: boolean;
}

/**
 * A simple DropdownButton replacement for react-bootstrap's <DropdownButton>.
 * Renders a standalone dropdown button with inline styles and basic accessibility.
 */
const DropdownButton: React.FC<DropdownButtonProps> = ({
  title,
  children,
  style = {},
  menuAlignRight = false,
  pullRight = false,
  noCaret = false,
  id,
  className = '',
  bsStyle,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // Ref for the main container div to detect clicks outside
  const ref = useRef<HTMLDivElement>(null);

  // Support legacy pullRight prop
  const alignRight = menuAlignRight || pullRight;

  // Effect to handle closing the dropdown when clicking outside
  useEffect(() => {
    // Function to check if the click occurred outside the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false); // Close the dropdown
      }
    }

    // Add event listener only when the dropdown is open
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Remove listener if dropdown is closed
      document.removeEventListener('mousedown', handleClickOutside);
    }
    // Cleanup function to remove listener when the component unmounts or `open` changes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]); // Dependency array ensures this effect runs only when `open` changes

  // Effect to close the dropdown when it becomes disabled
  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  // Style for the main container div
  const containerStyle: React.CSSProperties = {
    position: 'relative', // Needed for absolute positioning of the menu
    display: 'inline-block', // Allows the component to sit inline with other elements
    ...style, // Apply any user-provided style overrides
  };

  // Determine button style based on bsStyle prop
  const isLinkStyle = bsStyle === 'link';

  // Base button style matching Bootstrap 3 defaults
  const baseButtonStyle: React.CSSProperties = {
    display: 'inline-block',
    marginBottom: 0,
    fontWeight: 400,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    touchAction: 'manipulation',
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    backgroundImage: 'none',
    border: '1px solid transparent',
    fontSize: '14px',
    lineHeight: '1.42857143',
    borderRadius: '4px',
    padding: '6px 12px', // Bootstrap 3 default for 34px height
    transition: 'all 0.15s ease-in-out',
  };

  // Link style variant
  const linkButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    color: disabled ? '#777' : '#337ab7',
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    fontWeight: 400,
    textDecoration: isHovered ? 'underline' : 'none',
    opacity: disabled ? 0.65 : 1,
  };

  // Default (white) style variant
  const defaultButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    color: disabled ? '#333' : '#333',
    background: disabled ? '#e6e6e6' : isHovered ? '#e6e6e6' : '#fff',
    borderColor: '#ccc',
    opacity: disabled ? 0.65 : 1,
    boxShadow: isFocused
      ? '0 0 8px rgba(102, 175, 233, 0.6), inset 0 1px 1px rgba(0,0,0,.075)'
      : 'inset 0 1px 0 rgba(255,255,255,.15), 0 1px 1px rgba(0,0,0,.075)',
    borderColor: isFocused ? '#66afe9' : '#ccc',
  };

  const buttonStyle = isLinkStyle ? linkButtonStyle : defaultButtonStyle;

  // Style for the dropdown menu div
  const menuStyle: React.CSSProperties = {
    display: open ? 'block' : 'none',
    position: 'absolute',
    top: '100%',
    left: alignRight ? 'auto' : 0,
    right: alignRight ? 0 : 'auto',
    minWidth: 160,
    background: '#fff',
    border: '1px solid rgba(0,0,0,.15)',
    borderRadius: 4,
    boxShadow: '0 6px 12px rgba(0,0,0,.175)',
    marginTop: 2,
    zIndex: 1000,
    textAlign: 'left',
    padding: '5px 0',
    listStyle: 'none',
    fontSize: '14px',
    backgroundClip: 'padding-box',
  };

  return (
    <div style={containerStyle} ref={ref} className={className}>
      <button
        id={id}
        style={buttonStyle}
        onClick={() => !disabled && setOpen(v => !v)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={disabled}
        type="button"
      >
        {title}
        {!noCaret && (
          <span
            style={{
              display: 'inline-block',
              width: 0,
              height: 0,
              marginLeft: '4px',
              verticalAlign: 'middle',
              borderTop: '4px dashed',
              borderRight: '4px solid transparent',
              borderLeft: '4px solid transparent',
            }}
          />
        )}
      </button>
      <div style={menuStyle} role="menu">
        {children}
      </div>
    </div>
  );
};

export default DropdownButton;
